import type { AttorneyGeneralWizardDraft } from '../../../types/attorneyGeneralCaseRequest';
import {
  contactNumberError,
  exceedsMax,
  isFutureIsoDate,
  isTodayOrFutureIsoDate,
  maxCharLimitExceededError,
  todayIsoDateLocal,
} from '../../../utils/caseFieldValidation';
import type { RequiredErrors, RequiredFieldSpec } from '../../../utils/requiredValidation';
import { validateRequired } from '../../../utils/requiredValidation';
import {
  requiredFieldError,
  validationDateFutureOnly,
  validationDocReasonRequired,
} from '../../../utils/validationI18n';
import type { AttorneyGeneralWizardStep } from './attorneyGeneralConstants';
import {
  isHighUrgencyLevel,
  isStatusOther,
  numberOfRespondentsError,
  requiresCourtType,
} from './attorneyGeneralHelpers';
import type { LookupItem } from '../../../mocks/criminalMasterData';

const MAX_DESC = 2000;
const MAX_REF = 255;

export const MAX_DOC_SKIP_REASON = 255;

function emailAddressError(value: string): string | undefined {
  const t = value.trim();
  if (!t) return undefined;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t) ? undefined : 'Enter a valid email address.';
}

export function validateAttorneyGeneralStep(
  step: AttorneyGeneralWizardStep,
  draft: AttorneyGeneralWizardDraft,
  options?: { suspectStatuses?: LookupItem[] },
): { valid: boolean; errors: RequiredErrors; specs: RequiredFieldSpec[] } {
  const specs: RequiredFieldSpec[] = [];
  const extra: RequiredErrors = {};
  const suspectStatuses = options?.suspectStatuses ?? [];

  if (step === 1) {
    specs.push(
      { key: 'fileCategoryId', label: 'File Category', value: draft.fileCategoryId, elementId: 'ag-fileCategory' },
      { key: 'subFileCategoryId', label: 'Sub File Category', value: draft.subFileCategoryId, elementId: 'ag-subFileCategory' },
    );

    if (draft.hasPreviousAdvice === 'yes' && draft.previousAdviceUploads.length === 0) {
      extra.previousAdviceUploads = 'Previous advice attachment is required when Yes is selected.';
    }
    if (draft.hasConnectedAdviceCase === 'yes') {
      specs.push({
        key: 'connectedAdviceDetails',
        label: 'Connected Advice Case Details',
        value: draft.connectedAdviceDetails,
        elementId: 'ag-connectedAdviceDetails',
      });
    }
    if (requiresCourtType(draft.hasActionInstituted)) {
      specs.push({
        key: 'courtTypeId',
        label: 'Court Type',
        value: draft.courtTypeId,
        elementId: 'ag-courtType',
      });
    }
    if (draft.isAppealMatter === 'yes') {
      specs.push({
        key: 'originalCourt',
        label: 'Original Court',
        value: draft.originalCourt,
        elementId: 'ag-originalCourt',
      });
      if (draft.judgementOrderUploads.length === 0) {
        extra.judgementOrderUploads = 'Judgement/Order attachment is required when Yes is selected.';
      }
    }
    if (draft.hasConnectedMatters === 'yes') {
      specs.push(
        { key: 'connectedCaseNumber', label: 'Connected Case Number', value: draft.connectedCaseNumber, elementId: 'ag-connectedCase' },
        { key: 'connectedAgdFileRef', label: 'Connected AGD File Reference Number', value: draft.connectedAgdFileRef, elementId: 'ag-connectedAgd' },
      );
    }

    if (exceedsMax(draft.description, MAX_DESC)) extra.description = maxCharLimitExceededError();
    if (exceedsMax(draft.caseSource, MAX_DESC)) extra.caseSource = maxCharLimitExceededError();
    if (exceedsMax(draft.directionOfRequest, MAX_REF)) extra.directionOfRequest = maxCharLimitExceededError();
    if (exceedsMax(draft.yourReferenceNumber, MAX_REF)) extra.yourReferenceNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.otherExternalReferences, MAX_REF)) extra.otherExternalReferences = maxCharLimitExceededError();
    if (exceedsMax(draft.caseNumber, MAX_REF)) extra.caseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedCaseNumber, MAX_REF)) extra.connectedCaseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedAgdFileRef, MAX_REF)) extra.connectedAgdFileRef = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedAdviceDetails, MAX_DESC)) extra.connectedAdviceDetails = maxCharLimitExceededError();
    if (exceedsMax(draft.originalCourt, MAX_REF)) extra.originalCourt = maxCharLimitExceededError();
    if (exceedsMax(draft.courtTypeOther, MAX_REF)) extra.courtTypeOther = maxCharLimitExceededError();
  }

  if (step === 2) {
    specs.push(
      { key: 'partyBodyType', label: 'Party Body Type', value: draft.partyBodyType, elementId: 'ag-partyBodyType' },
    );

    if (exceedsMax(draft.plaintiffName, MAX_REF)) extra.plaintiffName = maxCharLimitExceededError();
    if (exceedsMax(draft.defendantName, MAX_REF)) extra.defendantName = maxCharLimitExceededError();
    if (exceedsMax(draft.otherInvolvedInstitutions, MAX_DESC)) extra.otherInvolvedInstitutions = maxCharLimitExceededError();
    if (exceedsMax(draft.legalOfficerName, MAX_REF)) extra.legalOfficerName = maxCharLimitExceededError();

    const contactErr = contactNumberError(draft.legalOfficerContact);
    if (contactErr) extra.legalOfficerContact = contactErr;
  }

  if (step === 3) {
    if (exceedsMax(draft.subjectOfDispute, MAX_DESC)) extra.subjectOfDispute = maxCharLimitExceededError();
    if (exceedsMax(draft.nextStepOfCaseOther, MAX_REF)) extra.nextStepOfCaseOther = maxCharLimitExceededError();
    if (draft.nextCaseDate && !isTodayOrFutureIsoDate(draft.nextCaseDate)) {
      extra.nextCaseDate = validationDateFutureOnly();
    }
  }

  if (step === 4) {
    if (exceedsMax(draft.victimName, MAX_REF)) extra.victimName = maxCharLimitExceededError();
  }

  if (step === 5) {
    if (draft.offenceDateFrom && isFutureIsoDate(draft.offenceDateFrom)) {
      extra.offenceDateFrom = 'Date of Offence cannot be in the future.';
    } else if (draft.offenceDateTo && isFutureIsoDate(draft.offenceDateTo)) {
      extra.offenceDateFrom = 'Date of Offence cannot be in the future.';
    }
    if (exceedsMax(draft.placeOfOffence, MAX_DESC)) extra.placeOfOffence = maxCharLimitExceededError();

    draft.accused.forEach((a, i) => {
      if (a.name.trim() && exceedsMax(a.name, MAX_DESC)) extra[`accused-name-${i}`] = maxCharLimitExceededError();
      if (isStatusOther(a.status, suspectStatuses) && !a.statusOther.trim()) {
        extra[`accused-statusOther-${i}`] = requiredFieldError('Status (Other)');
      }
    });
  }

  if (step === 6) {
    if (exceedsMax(draft.accusedName, MAX_REF)) extra.accusedName = maxCharLimitExceededError();
    if (exceedsMax(draft.passportOrIdNumber, MAX_REF)) extra.passportOrIdNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.custodialAuthority, MAX_REF)) extra.custodialAuthority = maxCharLimitExceededError();
    if (exceedsMax(draft.lastKnownLocation, MAX_DESC)) extra.lastKnownLocation = maxCharLimitExceededError();
  }

  if (step === 7) {
    if (draft.arrestWarrantAvailable === 'yes') {
      specs.push({
        key: 'warrantReference',
        label: 'Warrant Reference',
        value: draft.warrantReference,
        elementId: 'ag-warrantReference',
      });
    }
    if (exceedsMax(draft.warrantReference, MAX_REF)) extra.warrantReference = maxCharLimitExceededError();
    if (exceedsMax(draft.applicableTreatyArrangement, MAX_REF)) extra.applicableTreatyArrangement = maxCharLimitExceededError();
  }

  if (step === 8) {
    if (exceedsMax(draft.petitionerName, MAX_REF)) extra.petitionerName = maxCharLimitExceededError();
    if (exceedsMax(draft.petitionerNic, MAX_REF)) extra.petitionerNic = maxCharLimitExceededError();
    if (exceedsMax(draft.petitionerAddress, MAX_DESC)) extra.petitionerAddress = maxCharLimitExceededError();
    const petContactErr = contactNumberError(draft.petitionerContact);
    if (petContactErr) extra.petitionerContact = petContactErr;
    const emailErr = emailAddressError(draft.petitionerEmail);
    if (emailErr) extra.petitionerEmail = emailErr;
    if (draft.representedByAttorney === 'yes') {
      if (exceedsMax(draft.attorneyName, MAX_REF)) extra.attorneyName = maxCharLimitExceededError();
      const attContactErr = contactNumberError(draft.attorneyContact);
      if (attContactErr) extra.attorneyContact = attContactErr;
    }
    if (exceedsMax(draft.respondentNames, MAX_REF)) extra.respondentNames = maxCharLimitExceededError();
    if (exceedsMax(draft.respondentAddress, MAX_DESC)) extra.respondentAddress = maxCharLimitExceededError();
    if (draft.numberOfRespondents.trim()) {
      specs.push({
        key: 'numberOfRespondents',
        label: 'Number of Respondents',
        value: draft.numberOfRespondents,
        elementId: 'ag-numberOfRespondents',
      });
    }
    const respondentsErr = numberOfRespondentsError(draft.numberOfRespondents);
    if (respondentsErr) extra.numberOfRespondents = respondentsErr;
  }

  if (step === 9) {
    const rtiContactErr = contactNumberError(draft.rtiContactNumber);
    if (rtiContactErr) extra.rtiContactNumber = rtiContactErr;
    const rtiEmailErr = emailAddressError(draft.rtiEmailAddress);
    if (rtiEmailErr) extra.rtiEmailAddress = rtiEmailErr;

    if (exceedsMax(draft.rtiRequestorName, MAX_DESC)) extra.rtiRequestorName = maxCharLimitExceededError();
    if (exceedsMax(draft.rtiNicOrPassport, MAX_REF)) extra.rtiNicOrPassport = maxCharLimitExceededError();
    if (exceedsMax(draft.rtiAddress, MAX_DESC)) extra.rtiAddress = maxCharLimitExceededError();
    if (exceedsMax(draft.rtiSpecificPublicAuthorityName, MAX_REF)) extra.rtiSpecificPublicAuthorityName = maxCharLimitExceededError();
    if (exceedsMax(draft.rtiRequestedInformationDetails, MAX_DESC)) extra.rtiRequestedInformationDetails = maxCharLimitExceededError();
    if (exceedsMax(draft.rtiInformationRequestedTimePeriod, MAX_REF)) {
      extra.rtiInformationRequestedTimePeriod = maxCharLimitExceededError();
    }
    if (exceedsMax(draft.rtiJustification, MAX_DESC)) extra.rtiJustification = maxCharLimitExceededError();
    if (exceedsMax(draft.rtiAdditionalNotes, MAX_DESC)) extra.rtiAdditionalNotes = maxCharLimitExceededError();
  }

  if (step === 10) {
    if (isHighUrgencyLevel(draft.urgencyLevelName)) {
      specs.push({
        key: 'urgencyJustification',
        label: 'Urgency Justification',
        value: draft.urgencyJustification,
        elementId: 'ag-urgencyJustification',
      });
    }

    if (exceedsMax(draft.urgencyJustification, MAX_DESC)) extra.urgencyJustification = maxCharLimitExceededError();
    if (draft.deadline && !isTodayOrFutureIsoDate(draft.deadline)) {
      extra.deadline = validationDateFutureOnly();
    }
  }

  if (step === 12) {
    Object.assign(extra, validateStep12Documents(draft));
  }

  const reqErrs = validateRequired(specs);
  const errors = { ...reqErrs, ...extra };
  return { valid: Object.keys(errors).length === 0, errors, specs };
}

export function validateStep12Documents(draft: AttorneyGeneralWizardDraft): RequiredErrors {
  const errors: RequiredErrors = {};
  draft.documents.forEach((doc) => {
    if (doc.fileName) return;
    if (doc.mandatory && !doc.skipReason.trim()) {
      errors[`doc-reason-${doc.id}`] = validationDocReasonRequired();
      return;
    }
    if (exceedsMax(doc.skipReason, MAX_DOC_SKIP_REASON)) {
      errors[`doc-reason-${doc.id}`] = maxCharLimitExceededError();
    }
  });
  return errors;
}

/** @deprecated use validateStep12Documents */
export const validateStep5Documents = validateStep12Documents;

export function minDeadlineDateForAttorneyGeneral(): string {
  return todayIsoDateLocal();
}
